import { Box, HStack, Text } from '@chakra-ui/react'
import { type NodeProps, NodeResizer, useReactFlow } from '@xyflow/react'
import { Palette } from 'lucide-react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useColorModeValue } from '~/color-mode'

export interface GroupNodeData {
  label: string
  backgroundColor: string
}

const GROUP_COLORS = [
  { name: 'Gray', value: 'rgba(200, 200, 200, 0.3)' },
  { name: 'Blue', value: 'rgba(59, 130, 246, 0.2)' },
  { name: 'Green', value: 'rgba(34, 197, 94, 0.2)' },
  { name: 'Yellow', value: 'rgba(234, 179, 8, 0.2)' },
  { name: 'Red', value: 'rgba(239, 68, 68, 0.2)' },
  { name: 'Purple', value: 'rgba(168, 85, 247, 0.2)' },
]

function GroupNode({ id, data, selected }: NodeProps) {
  const { setNodes } = useReactFlow()
  const [isEditing, setIsEditing] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const colorPickerRef = useRef<HTMLDivElement>(null)

  const label = (data?.label as string) || 'New Group'
  const backgroundColor = (data?.backgroundColor as string) || 'rgba(200, 200, 200, 0.3)'

  // Chrome colors (label input + swatch selection, not diagram data) - theme-aware for dark mode
  const labelInputTextColor = useColorModeValue('#5F5D5A', '#CDCCC9')
  const swatchSelectedBg = useColorModeValue('#F0F0EF', '#4A4743')

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false)
      }
    }

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showColorPicker])

  const handleLabelChange = useCallback(
    (newLabel: string) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                label: newLabel,
              },
            }
          }
          return node
        }),
      )
    },
    [id, setNodes],
  )

  const handleColorChange = useCallback(
    (newColor: string) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                backgroundColor: newColor,
              },
            }
          }
          return node
        }),
      )
      setShowColorPicker(false)
    },
    [id, setNodes],
  )

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }, [])

  const handleInputBlur = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsEditing(false)
    }
  }, [])

  return (
    <>
      <NodeResizer
        minWidth={150}
        minHeight={100}
        isVisible={selected}
        lineClassName="group-node-resize-line"
        handleClassName="group-node-resize-handle"
      />
      <Box
        className="group-node"
        w="100%"
        h="100%"
        bg={backgroundColor}
        borderRadius="8px"
        border="2px dashed"
        borderColor={selected ? 'highlight.solid' : 'rgba(150, 150, 150, 0.5)'}
        position="relative"
      >
        {/* Label */}
        <Box
          className="group-node-label"
          position="absolute"
          top="-24px"
          left="8px"
          bg="bg.panel"
          px={2}
          py={0.5}
          borderRadius="4px"
          boxShadow="0 1px 3px rgba(0,0,0,0.1)"
          onDoubleClick={handleDoubleClick}
          cursor="text"
        >
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              style={{
                border: 'none',
                outline: '2px solid var(--chakra-colors-yellow-400, #FFC111)',
                outlineOffset: '2px',
                borderRadius: '4px',
                background: 'transparent',
                fontSize: '12px',
                fontWeight: 500,
                color: labelInputTextColor,
                width: Math.max(60, label.length * 8),
              }}
            />
          ) : (
            <Text fontSize="12px" fontWeight="500" color="gray.700">
              {label}
            </Text>
          )}
        </Box>

        {/* Color picker button */}
        {selected && (
          <Box className="group-node-color-picker" position="absolute" top="-24px" right="8px">
            <button
              type="button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              style={{
                background: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 6px',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Palette size={12} />
            </button>

            {/* Color picker dropdown */}
            {showColorPicker && (
              <Box
                ref={colorPickerRef}
                position="absolute"
                top="28px"
                right="0"
                bg="bg.panel"
                borderRadius="8px"
                boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                p={2}
                zIndex={10000}
                minW="120px"
              >
                <Text fontSize="xs" color="content.secondary" mb={2} px={1}>
                  Group Color
                </Text>
                {GROUP_COLORS.map((color) => (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => handleColorChange(color.value)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor:
                        backgroundColor === color.value ? swatchSelectedBg : 'transparent',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    <Box
                      w="16px"
                      h="16px"
                      borderRadius="4px"
                      bg={color.value}
                      border="1px solid rgba(0,0,0,0.1)"
                    />
                    <span>{color.name}</span>
                  </button>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  )
}

export default memo(GroupNode)
